import { useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

/**
 * Migration status values.
 * Flow: uploaded → mapping → validating → validated → executing → completed/failed
 */
export type MigrationStatus =
  | 'uploaded'
  | 'mapping'
  | 'validating'
  | 'validated'
  | 'executing'
  | 'completed'
  | 'failed';

/**
 * Entity types supported by the migration importer. Mirrors the backend
 * MIGRATION_ENTITY_TYPES enum in migration.schema.ts.
 */
export const MIGRATION_ENTITY_TYPES = [
  'clients',
  'contacts',
  'users',
  'teams',
  'jobs',
  'invoices',
  'payments',
  'credit_notes',
  'time_entries',
] as const;

export type MigrationEntityType = (typeof MIGRATION_ENTITY_TYPES)[number];

/**
 * Field mapping entry: source column → target field.
 * The backend expects a flat Record<sourceColumn, targetField>; the UI works
 * with an array for ergonomic rendering and converts on submit.
 */
export interface FieldMapping {
  sourceColumn: string;
  targetField: string;
  /**
   * UI-only flag indicating the target is a custom form-builder field (stored
   * in form_data) rather than a known real column. Not sent to the API — the
   * backend folds any non-column target into form_data automatically.
   */
  isCustom?: boolean;
}

/**
 * A single row/field-level validation or execution error, as stored by the
 * backend in validation_report.errors / error_details.
 */
export interface MigrationErrorDetail {
  row: number;
  field: string;
  message: string;
  value?: unknown;
}

/**
 * The migration_imports record as returned by the API (snake_case).
 */
export interface MigrationImport {
  id: string;
  tenant_id: string;
  source_system: string;
  entity_type: MigrationEntityType;
  entity_type_id: string | null;
  file_name: string | null;
  file_size_bytes: number | null;
  field_mapping: Record<string, string> | null;
  status: MigrationStatus;
  total_records: number;
  processed_records: number;
  success_records: number;
  error_records: number;
  skipped_records: number;
  validation_report: {
    total_records?: number;
    valid_records?: number;
    invalid_records?: number;
    skipped_duplicates?: number;
    errors?: MigrationErrorDetail[];
  } | null;
  error_details: MigrationErrorDetail[] | null;
  started_at: string | null;
  completed_at: string | null;
  created_by_id: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Upload response from POST /migration/upload — the created import record plus
 * the parsed CSV columns and a small sample for the mapping step.
 */
export interface UploadResponse extends MigrationImport {
  columns: string[];
  sample_data: Record<string, string>[];
}

/**
 * Lightweight progress from GET /migration/:id/status (used for polling).
 */
export interface MigrationStatusResponse {
  id: string;
  status: MigrationStatus;
  progress: number;
  processed_records: number;
  total_records: number;
  success_records: number;
  error_records: number;
  skipped_records: number;
  started_at: string | null;
  completed_at: string | null;
}

/**
 * Report from GET /migration/:id/report.
 */
export interface MigrationReport {
  import_id: string;
  status: MigrationStatus;
  entity_type: MigrationEntityType;
  total_records: number;
  processed_records: number;
  success_records: number;
  error_records: number;
  skipped_records: number;
  validation_report: MigrationImport['validation_report'];
  error_details: MigrationErrorDetail[] | null;
  started_at: string | null;
  completed_at: string | null;
}

/** Pagination metadata returned by the list endpoint. */
export interface MigrationPagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

/** Result of listing past migration imports (history). */
export interface MigrationListResult {
  imports: MigrationImport[];
  pagination: MigrationPagination;
}

/** Filters accepted by the list endpoint. */
export interface MigrationListParams {
  page?: number;
  pageSize?: number;
  status?: MigrationStatus;
  entity_type?: MigrationEntityType;
}

// Query keys
const migrationKeys = {
  all: ['migration'] as const,
  list: (params: MigrationListParams) => [...migrationKeys.all, 'list', params] as const,
  status: (id: string) => [...migrationKeys.all, 'status', id] as const,
  report: (id: string) => [...migrationKeys.all, 'report', id] as const,
};

/** Terminal statuses that should stop polling. */
function isTerminal(status?: MigrationStatus): boolean {
  return status === 'completed' || status === 'failed' || status === 'validated';
}

/**
 * Max consecutive polls WITHOUT any progress before we give up (2s × 150 ≈ 5
 * min). The cap is reset whenever progress advances, so a long-running but
 * healthy import (rows still being processed) keeps polling — only a truly
 * stalled import (worker down, no progress for ~5 min) stops.
 */
const MAX_STALL_POLLS = 150;

/**
 * Convert the UI's FieldMapping[] into the backend's Record<source, target>,
 * dropping unmapped columns.
 */
export function toFieldMappingRecord(mappings: FieldMapping[]): Record<string, string> {
  return mappings.reduce<Record<string, string>>((acc, m) => {
    if (m.targetField) acc[m.sourceColumn] = m.targetField;
    return acc;
  }, {});
}

/**
 * Hook to upload a migration file. Sends the file plus the selected entity
 * type as multipart/form-data to POST /migration/upload.
 */
export function useUploadMigrationFile() {
  return useMutation({
    mutationFn: async ({
      file,
      entityType,
      entityTypeId,
    }: {
      file: File;
      entityType: MigrationEntityType;
      entityTypeId?: string | null;
    }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', entityType);
      if (entityTypeId) formData.append('entity_type_id', entityTypeId);
      const response = await apiClient.post<ApiResponse<UploadResponse>>(
        '/migration/upload',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data.data;
    },
  });
}

/**
 * Hook to download a CSV template for an entity type. Includes the dynamic
 * form-builder field columns when an entity_type_id is provided. Triggers a
 * browser download of migration-template-<entity>.csv.
 */
export function useDownloadTemplate() {
  return useMutation({
    mutationFn: async ({
      entityType,
      entityTypeId,
    }: {
      entityType: MigrationEntityType;
      entityTypeId?: string | null;
    }) => {
      const params = new URLSearchParams({ entity_type: entityType });
      if (entityTypeId) params.set('entity_type_id', entityTypeId);
      const response = await apiClient.get(`/migration/template?${params.toString()}`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data as BlobPart], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `migration-template-${entityType}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
  });
}

/**
 * Hook to submit field mappings to POST /migration/:id/map-fields.
 */
export function useMapFields() {
  return useMutation({
    mutationFn: async (data: { id: string; mappings: FieldMapping[] }) => {
      const response = await apiClient.post<ApiResponse<MigrationImport>>(
        `/migration/${data.id}/map-fields`,
        { field_mapping: toFieldMappingRecord(data.mappings) },
      );
      return response.data.data;
    },
  });
}

/**
 * Hook to trigger validation via POST /migration/:id/validate.
 * Validation runs asynchronously; poll useMigrationStatus for completion.
 */
export function useValidateMigration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post<ApiResponse<MigrationImport>>(
        `/migration/${id}/validate`,
      );
      return response.data.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: migrationKeys.status(data.id) });
    },
  });
}

/**
 * Hook to start execution via POST /migration/:id/execute.
 * Execution runs asynchronously; poll useMigrationStatus for progress.
 */
export function useExecuteMigration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post<ApiResponse<MigrationImport>>(
        `/migration/${id}/execute`,
      );
      return response.data.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: migrationKeys.status(data.id) });
    },
  });
}

/**
 * Hook to poll migration progress via GET /migration/:id/status.
 * Stops polling once the import reaches a terminal state.
 */
export function useMigrationStatus(id: string | null, enabled = false) {
  // Stall tracking: remember the highest progress seen and how many polls have
  // happened without it advancing. Reset when progress moves so a healthy but
  // slow import never times out — only a genuinely stalled one does.
  const stallRef = useRef({ maxProgress: -1, stalledPolls: 0 });

  return useQuery({
    queryKey: migrationKeys.status(id ?? ''),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<MigrationStatusResponse>>(
        `/migration/${id}/status`,
      );
      return response.data.data;
    },
    enabled: !!id && enabled,
    refetchInterval: (query) => {
      const data = query.state.data;

      // Stop once the import reaches a terminal state.
      if (isTerminal(data?.status)) {
        stallRef.current = { maxProgress: -1, stalledPolls: 0 };
        return false;
      }

      // Progress-aware stall detection. Every time progress advances we reset
      // the stall counter; if it hasn't moved for MAX_STALL_POLLS consecutive
      // polls (~5 min at 2s), assume the worker died and stop polling so we
      // don't hammer the endpoint forever. A long import that keeps advancing
      // keeps polling indefinitely.
      const progress = data?.progress ?? 0;
      if (progress > stallRef.current.maxProgress) {
        stallRef.current.maxProgress = progress;
        stallRef.current.stalledPolls = 0;
      } else {
        stallRef.current.stalledPolls += 1;
      }
      if (stallRef.current.stalledPolls >= MAX_STALL_POLLS) return false;

      return 2000;
    },
  });
}

/**
 * Hook to fetch the final migration report via GET /migration/:id/report.
 */
export function useMigrationReport(id: string | null) {
  return useQuery({
    queryKey: migrationKeys.report(id ?? ''),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<MigrationReport>>(`/migration/${id}/report`);
      return response.data.data;
    },
    enabled: !!id,
  });
}

/** List response shape: data array + a sibling pagination object. */
interface MigrationListApiResponse {
  success: boolean;
  data: MigrationImport[];
  pagination: MigrationPagination;
}

/**
 * Hook to list past migration imports (history) via GET /migration.
 * Paginated and optionally filtered by status / entity_type.
 */
export function useMigrationList(params: MigrationListParams = {}, enabled = true) {
  return useQuery({
    queryKey: migrationKeys.list(params),
    queryFn: async (): Promise<MigrationListResult> => {
      const search = new URLSearchParams();
      if (params.page) search.set('page', String(params.page));
      if (params.pageSize) search.set('pageSize', String(params.pageSize));
      if (params.status) search.set('status', params.status);
      if (params.entity_type) search.set('entity_type', params.entity_type);
      const qs = search.toString();
      const response = await apiClient.get<MigrationListApiResponse>(
        `/migration${qs ? `?${qs}` : ''}`,
      );
      return { imports: response.data.data, pagination: response.data.pagination };
    },
    enabled,
  });
}

/**
 * Per-role outcome from a roles & permissions import.
 */
export interface RoleImportResult {
  name: string;
  action: 'created' | 'updated' | 'skipped';
  permission_count: number;
  data_scope?: string;
  message?: string;
}

/**
 * Full report returned by POST /migration/roles-permissions/import.
 */
export interface RolesImportReport {
  total_roles: number;
  created: number;
  updated: number;
  skipped: number;
  results: RoleImportResult[];
  warnings: { row: number; message: string }[];
}

/**
 * Hook to download the roles & permissions matrix template. Triggers a browser
 * download of roles-permissions-template.csv (every permission as a row, with
 * example role columns pre-filled Yes/No).
 */
export function useDownloadRolesTemplate() {
  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.get('/migration/roles-permissions/template', {
        responseType: 'blob',
      });
      const blob = new Blob([response.data as BlobPart], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'roles-permissions-template.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
  });
}

/**
 * Hook to import roles from a filled permissions matrix CSV via
 * POST /migration/roles-permissions/import. Runs synchronously server-side and
 * returns the per-role created/updated/skipped report.
 */
export function useImportRolesPermissions() {
  return useMutation({
    mutationFn: async (file: File): Promise<RolesImportReport> => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post<ApiResponse<RolesImportReport>>(
        '/migration/roles-permissions/import',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data.data;
    },
  });
}

/**
 * Per-row outcome from a users-with-teams import.
 */
export interface UserTeamImportResult {
  row: number;
  team: string;
  user: string;
  status: 'imported' | 'skipped';
  message: string;
}

/**
 * Full report returned by POST /migration/users-teams/import.
 */
export interface UsersTeamsImportReport {
  total_rows: number;
  imported: number;
  skipped: number;
  teams_created: number;
  users_created: number;
  results: UserTeamImportResult[];
  warnings: { row: number; field: string; message: string }[];
}

/**
 * Hook to download the users-with-teams template CSV. Triggers a browser
 * download of users-teams-template.csv.
 */
export function useDownloadUsersTeamsTemplate() {
  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.get('/migration/users-teams/template', {
        responseType: 'blob',
      });
      const blob = new Blob([response.data as BlobPart], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'users-teams-template.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
  });
}

/**
 * Hook to import users + teams from a filled CSV via
 * POST /migration/users-teams/import. Runs synchronously server-side and
 * returns the per-row imported/skipped report.
 */
export function useImportUsersTeams() {
  return useMutation({
    mutationFn: async (file: File): Promise<UsersTeamsImportReport> => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post<ApiResponse<UsersTeamsImportReport>>(
        '/migration/users-teams/import',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data.data;
    },
  });
}

/** Per-row prediction from the users-with-teams dry-run preview. */
export interface UserTeamPreviewRow {
  row: number;
  team: string;
  user: string;
  teamAction: 'create' | 'existing';
  userAction: 'create' | 'existing';
  teamJobRole: string;
  isTeamLead: boolean;
  roleResolved: boolean;
  roleName: string;
}

/** Dry-run preview returned by POST /migration/users-teams/preview. */
export interface UsersTeamsPreview {
  total_rows: number;
  teams_to_create: number;
  users_to_create: number;
  rows_with_unresolved_role: number;
  rows: UserTeamPreviewRow[];
  warnings: { row: number; field: string; message: string }[];
}

/**
 * Hook to dry-run (validate) a users-with-teams file via
 * POST /migration/users-teams/preview. Writes nothing; returns predictions.
 */
export function usePreviewUsersTeams() {
  return useMutation({
    mutationFn: async (file: File): Promise<UsersTeamsPreview> => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post<ApiResponse<UsersTeamsPreview>>(
        '/migration/users-teams/preview',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data.data;
    },
  });
}
